import React, { useEffect, useRef, useState } from "react";
import {
  Typography,
  Radio,
  Checkbox,
  Input,
  DatePicker,
  TimePicker,
  InputNumber,
  Button,
  Space,
  Segmented,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import {
  Survey,
  QuestionData,
  SectionData,
  Answer,
} from "../../../types/survey";
import clsx from "clsx";
import dayjs from "dayjs";
import ImageUploadWithMetadata from "../ImageUploadWithMetadata";
import { FileMetadataBody } from "../../../hooks/api/responses";

import styles from "./index.module.scss";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface SurveyPreviewProps {
  survey: Survey;
  readOnly?: boolean;
  isTemplate?: boolean;
  isResponse?: boolean;
  onSubmit?: (answers: Record<string, any>) => void;
  onAnswersChange?: (answers: Record<string, any>) => void;
  initialAnswers?: Record<string, any>;
  onFileMetadataChange?: (
    fileId: string,
    metadata: Partial<FileMetadataBody>,
  ) => void;
  pendingFileMetadata?: Record<string, Partial<FileMetadataBody>>;
}

const isCustomOptionAnswer = (answer: Answer): boolean => {
  return !!(
    answer.options?.multiline === true &&
    typeof answer.options?.minLength === "number" &&
    answer.options.minLength > 0 &&
    typeof answer.options?.maxLength === "number" &&
    answer.options.maxLength > 0
  );
};

const QuestionPreview: React.FC<{
  question: QuestionData;
  index: number;
  value?: any;
  isResponse?: boolean;
  onChange?: (value: any) => void;
  readOnly?: boolean;
  onFileMetadataChange?: (
    fileId: string,
    metadata: Partial<FileMetadataBody>,
  ) => void;
  pendingFileMetadata?: Record<string, Partial<FileMetadataBody>>;
}> = ({
  question,
  index,
  value,
  onChange,
  readOnly,
  isResponse,
  onFileMetadataChange,
  pendingFileMetadata,
}) => {
    const renderInput = () => {
      switch (question.type) {
        case "toggle": {
          const yesAnswer = question.answers?.find(
            (a) => a.title === "Yes" || a.title === "yes",
          );
          const noAnswer = question.answers?.find(
            (a) => a.title === "No" || a.title === "no",
          );

          const yesId = yesAnswer?.id || "yes";
          const noId = noAnswer?.id || "no";

          const currentValue = value || yesId;

          return (
            <Segmented
              size="large"
              style={{ width: "240px" }}
              value={currentValue}
              onChange={(val) => onChange?.(val)}
              block
              options={[
                { label: "Yes", value: yesId },
                { label: "No", value: noId },
              ]}
            />
          );
        }
        case "singleSelect": {
          const customAnswer = question.answers?.find(isCustomOptionAnswer);
          const regularAnswers =
            question.answers?.filter((a) => !isCustomOptionAnswer(a)) || [];

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const customTextRef = useRef<string>("");

          const isObjectValue = typeof value === "object" && value !== null;
          const isCustomSelected = isObjectValue && value.isCustom === true;
          const selectedId = isObjectValue ? value.id : value;
          const customText = isObjectValue ? value.text : "";

          if (isCustomSelected && customText) {
            customTextRef.current = customText;
          }

          return (
            <div style={{ padding: "0 0 0 16px" }}>
              <Radio.Group
                style={{ width: "100%" }}
                value={selectedId}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (customAnswer && newValue === customAnswer.id) {
                    onChange?.({
                      id: customAnswer.id,
                      text: customTextRef.current,
                      isCustom: true,
                    });
                  } else {
                    if (isCustomSelected) {
                      customTextRef.current = customText;
                    }
                    onChange?.(newValue);
                  }
                }}
                disabled={readOnly}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {regularAnswers.map((answer) => (
                    <Radio key={answer.id} value={answer.id}>
                      {answer.title}
                    </Radio>
                  ))}
                  {customAnswer && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      <Radio value={customAnswer.id}>{customAnswer.title}</Radio>
                      {isCustomSelected && selectedId === customAnswer.id && (
                        <TextArea
                          value={customText}
                          onChange={(e) => {
                            customTextRef.current = e.target.value; // ← сохраняем в ref
                            onChange?.({
                              id: customAnswer.id,
                              text: e.target.value,
                              isCustom: true,
                            });
                          }}
                          placeholder="Enter your answer"
                          disabled={readOnly}
                          rows={1}
                          style={{
                            width: "100%",
                            marginTop: 8,
                          }}
                        />
                      )}
                    </div>
                  )}
                </Space>
              </Radio.Group>
            </div>
          );
        }

        case "multiSelect": {
          const customAnswer = question.answers?.find(isCustomOptionAnswer);
          const regularAnswers =
            question.answers?.filter((a) => !isCustomOptionAnswer(a)) || [];

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const customTextRef = useRef<string>("");

          const values: any[] = value || [];

          const customValue = values.find(
            (v: any) =>
              typeof v === "object" && v.isCustom && v.id === customAnswer?.id,
          );

          if (customValue?.text) {
            customTextRef.current = customValue.text;
          }

          const isCustomSelected = !!customValue;
          const customText = customValue?.text ?? customTextRef.current;

          const selectedIds = values.map((v: any) =>
            typeof v === "object" ? v.id : v,
          );

          return (
            <div style={{ padding: "0 0 0 16px" }}>
              <Checkbox.Group
                style={{ width: "100%" }}
                value={selectedIds}
                onChange={(checkedIds: any[]) => {
                  const newValues = checkedIds.map((id: string) => {
                    if (customAnswer && id === customAnswer.id) {
                      return {
                        id: customAnswer.id,
                        text: customTextRef.current,
                        isCustom: true,
                      };
                    }
                    return id;
                  });
                  const result = newValues.filter((v: any) => {
                    const id = typeof v === "object" ? v.id : v;
                    return checkedIds.includes(id);
                  });

                  onChange?.(result);
                }}
                disabled={readOnly}
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {regularAnswers.map((answer) => (
                    <Checkbox key={answer.id} value={answer.id}>
                      {answer.title}
                    </Checkbox>
                  ))}
                  {customAnswer && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      <Checkbox value={customAnswer.id}>
                        {customAnswer.title}
                      </Checkbox>
                      {isCustomSelected && (
                        <TextArea
                          value={customText}
                          onChange={(e) => {
                            customTextRef.current = e.target.value;
                            const newValues = values.map((v: any) => {
                              if (
                                typeof v === "object" &&
                                v.id === customAnswer.id
                              ) {
                                return { ...v, text: e.target.value };
                              }
                              return v;
                            });
                            onChange?.(newValues);
                          }}
                          placeholder="Enter your answer"
                          disabled={readOnly}
                          rows={1}
                          style={{
                            width: "100%",
                            marginTop: 8,
                          }}
                        />
                      )}
                    </div>
                  )}
                </Space>
              </Checkbox.Group>
            </div>
          );
        }

        case "text":
          const isMultiline =
            question.config?.multiline || question.options?.multiline;
          return (
            <TextArea
              value={value}
              onChange={(e) => onChange?.(e.target.value)}
              placeholder="Enter your answer"
              disabled={readOnly}
              rows={1}
            />
          );

        case "number":
          return (
            <InputNumber
              value={value}
              onChange={(val) => onChange?.(val)}
              min={question.config?.min ?? question.options?.min}
              max={question.config?.max ?? question.options?.max}
              disabled={readOnly}
              className={styles.input}
            />
          );

        case "date":
          return (
            <DatePicker
              value={value}
              onChange={(date) => onChange?.(date)}
              disabled={readOnly}
              className={styles.input}
            />
          );

        case "time":
          const parseTime = (val: string | dayjs.Dayjs | null) => {
            if (!val) return null;
            if (dayjs.isDayjs(val)) return val;

            const formats = ["HH:mm:ss", "HH:mm", "h:mm:ss A", "h:mm A"];
            for (const fmt of formats) {
              const parsed = dayjs(val, fmt);
              if (parsed.isValid()) return parsed;
            }
            return null;
          };
          const timeValue = parseTime(value);

          return (
            <TimePicker
              value={timeValue}
              onChange={(time) => {
                const timeString = time ? time.format("HH:mm:ss") : null;
                onChange?.(timeString);
              }}
              disabled={readOnly}
              className={styles.input}
              format="HH:mm:ss"
            />
          );

        case "image": {
          const normalizeFiles = (input: any[]): any[] => {
            if (!input) return [];
            return input.map((f, index) => ({
              uid: f.id || f.uid || `${Date.now()}_${index}`,
              name: f.originalName || f.name || "image",
              status: "done" as const,
              url: f.location || f.url || f.data || "",
              note: f.note || f.description || "",
              tags: f.tags || [],
            }));
          };

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const [fileList, setFileList] = useState<any[]>([]);
          // eslint-disable-next-line react-hooks/rules-of-hooks
          const prevFileIdsRef = useRef<string[]>([]);

          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            const files = question.files || [];
            if (files.length > 0 && fileList.length === 0) {
              setFileList(normalizeFiles(files));
            }
          }, [question.files]);
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            const fileIds = fileList.map((f) => f.uid).filter(Boolean);

            const isValidUUID = (id: string) => {
              const uuidRegex =
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
              return uuidRegex.test(id);
            };

            const allValidUUIDs = fileIds.every(isValidUUID);

            if (!allValidUUIDs) return;

            const isChanged =
              fileIds.length !== prevFileIdsRef.current.length ||
              fileIds.some((id, i) => id !== prevFileIdsRef.current[i]);

            if (isChanged) {
              prevFileIdsRef.current = fileIds;
              onChange?.(fileIds);
            }
          }, [fileList, onChange]);

          return (
            <div style={{ padding: "0 0 0 16px" }}>
              <ImageUploadWithMetadata
                name={question.id}
                fileList={fileList}
                setFileList={setFileList}
                readOnly={readOnly}
                multiple={true}
                wide={false}
                onFileMetadataChange={onFileMetadataChange}
                pendingMetadata={pendingFileMetadata}
              />
            </div>
          );
        }

        default:
          return <Text type="secondary">Unknown question type</Text>;
      }
    };

    return (
      <div
        style={{
          backgroundColor: "#F5F5F5",
          padding: 16,
          borderRadius: 6,
        }}
      >
        <Text strong style={{ display: "block", marginBottom: 12 }}>
          {index + 1}. {question.title}
          {question.required && <span style={{ color: "#ff4d4f" }}> *</span>}
        </Text>
        {renderInput()}
      </div>
    );
  };

const SectionPreview: React.FC<{
  section: SectionData;
  startIndex: number;
  isResponse?: boolean;
  answers: Record<string, any>;
  onAnswerChange: (questionId: string, value: any) => void;
  readOnly?: boolean;
  onFileMetadataChange?: (
    fileId: string,
    metadata: Partial<FileMetadataBody>,
  ) => void;
  pendingFileMetadata?: Record<string, Partial<FileMetadataBody>>;
}> = ({
  section,
  startIndex,
  answers,
  onAnswerChange,
  readOnly,
  isResponse,
  onFileMetadataChange,
  pendingFileMetadata,
}) => {
    return (
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: 6,
          padding: 16,
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ fontSize: 16 }}>
            {section.title || "Untitled Section"}
          </Text>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {section.questions.map((q: QuestionData, idx: number) => (
            <QuestionPreview
              isResponse={isResponse}
              key={q.id}
              question={q}
              index={startIndex + idx}
              value={answers[q.id]}
              onChange={(val) => onAnswerChange(q.id, val)}
              readOnly={readOnly}
              onFileMetadataChange={onFileMetadataChange}
              pendingFileMetadata={pendingFileMetadata}
            />
          ))}
        </div>
      </div>
    );
  };
const QuestionGroup: React.FC<{
  questions: QuestionData[];
  startIndex: number;
  isResponse?: boolean;
  answers: Record<string, any>;
  onAnswerChange: (questionId: string, value: any) => void;
  readOnly?: boolean;
  onFileMetadataChange?: (
    fileId: string,
    metadata: Partial<FileMetadataBody>,
  ) => void;
  pendingFileMetadata?: Record<string, Partial<FileMetadataBody>>;
}> = ({
  questions,
  startIndex,
  answers,
  onAnswerChange,
  readOnly,
  isResponse,
  onFileMetadataChange,
  pendingFileMetadata,
}) => {
    return (
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: 6,
          padding: 16,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {questions.map((q, idx) => (
            <QuestionPreview
              key={q.id}
              question={q}
              index={startIndex + idx}
              value={answers[q.id]}
              onChange={(val) => onAnswerChange(q.id, val)}
              readOnly={readOnly}
              isResponse={isResponse}
              onFileMetadataChange={onFileMetadataChange}
              pendingFileMetadata={pendingFileMetadata}
            />
          ))}
        </div>
      </div>
    );
  };

export const SurveyPreview: React.FC<SurveyPreviewProps> = ({
  survey,
  isTemplate,
  readOnly = false,
  onSubmit,
  initialAnswers,
  isResponse,
  onAnswersChange,
  onFileMetadataChange,
  pendingFileMetadata,
}) => {
  const [answers, setAnswers] = useState<Record<string, any>>(
    initialAnswers || {},
  );
  useEffect(() => {
    onAnswersChange?.(answers);
  }, [answers, onAnswersChange]);

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const renderGroupedItems = () => {
    const result: React.ReactNode[] = [];
    let currentQuestionGroup: QuestionData[] = [];
    let questionIndex = 0;

    const flushGroup = (startIndex: number) => {
      if (currentQuestionGroup.length > 0) {
        result.push(
          <QuestionGroup
            isResponse={isResponse}
            key={`group-${result.length}`}
            questions={currentQuestionGroup}
            startIndex={startIndex - currentQuestionGroup.length}
            answers={answers}
            onAnswerChange={handleAnswerChange}
            readOnly={readOnly}
            onFileMetadataChange={onFileMetadataChange}
            pendingFileMetadata={pendingFileMetadata}
          />,
        );
        currentQuestionGroup = [];
      }
    };

    survey.items.forEach((item) => {
      if (item.section) {
        flushGroup(questionIndex);
        const startIdx = questionIndex;
        questionIndex += item.section.questions.length;
        result.push(
          <SectionPreview
            isResponse={isResponse}
            key={item.id}
            section={item.section}
            startIndex={startIdx}
            answers={answers}
            onAnswerChange={handleAnswerChange}
            readOnly={readOnly}
            onFileMetadataChange={onFileMetadataChange}
            pendingFileMetadata={pendingFileMetadata}
          />,
        );
      } else if (item.question) {
        currentQuestionGroup.push(item.question);
        questionIndex++;
      }
    });

    flushGroup(questionIndex);

    return result;
  };

  const hasAnswers = initialAnswers && Object.keys(initialAnswers).length > 0;

  return (
    <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <div
        className={clsx(
          styles.defaultContainer,
          hasAnswers && styles.fullWidthContainer,
        )}
      >
        <Title level={4} style={{ marginBottom: 24 }}>
          {survey.title ||
            (isTemplate ? "Untitled Template" : "Untitled Survey")}
        </Title>
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {renderGroupedItems()}
        </div>
      </div>
    </div>
  );
};
